import React, { useRef } from 'react';
import { Box, CircularProgress } from '@/ui';
import Header from '@/components/Header';
import Footer from '@/components/on-boarding/Footer';
import { OuterNavChips, InnerNavDesktop, SectionWarnings } from './';
import type { ResolvedWarning, WarningToken } from '@/shared/warnings/types';

interface Route {
  key: string;
  label: string;
}

interface DesktopLayoutProps {
  outerRoutes: Route[];
  selectedOuter: string;
  currentInnerRoutes: Route[];
  selectedInner: string;
  sectionWarnings: ResolvedWarning[];
  nextButtonLabel: string;
  currentFormId: string;
  nextButtonLoading: boolean;
  nextButtonDisabled: boolean;
  isNavigating: boolean;
  onOuterRouteChange: (routeKey: string) => Promise<void>;
  onNavigateToInner: (key: string) => void;
  onSectionTokenClick: (token: WarningToken) => Promise<void>;
  onBackNav: () => Promise<void>;
  renderBudgetForm: () => React.ReactNode;
  /** When true, transforms "council tax" to "council rates" for NI users */
  isNorthernIreland?: boolean;
}

const DesktopLayout: React.FC<DesktopLayoutProps> = ({
  outerRoutes,
  selectedOuter,
  currentInnerRoutes,
  selectedInner,
  sectionWarnings,
  nextButtonLabel,
  currentFormId,
  nextButtonLoading,
  nextButtonDisabled,
  isNavigating,
  onOuterRouteChange,
  onNavigateToInner,
  onSectionTokenClick,
  onBackNav,
  renderBudgetForm,
  isNorthernIreland = false,
}) => {
  const desktopContentRef = useRef<HTMLDivElement | null>(null);

  return (
    <Box sx={{
      height: '100vh',
      width: '100vw',
      display: { xs: 'none', md: 'flex' },
      flexDirection: 'column',
      backgroundColor: '#f7f6f2',
      overflow: 'hidden'
    }}>
      {/* Header for Desktop */}
      <Header />

      {/* Content Area - Centered Container */}
      <Box ref={desktopContentRef} sx={{
        flex: 1,
        overflow: 'auto',
        backgroundColor: '#f7f6f2',
        px: 2,
        py: 2,
        width: '100%',
        minWidth: 0
      }}>
        <Box sx={{
          width: '100%',
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#f7f6f2',
          minHeight: 'min-content'
        }}>
          {/* Content */}
          <Box sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#f7f6f2',
            alignItems: 'center',
            width: '100%',
            minHeight: 0
          }}>
            <Box sx={{ 
              px: 3, 
              py: 2,
              width: '100%',
              maxWidth: '900px'
            }}>
              {/* Top Navigation - Outer Routes */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ 
                  display: 'flex', 
                  gap: 1, 
                  flexWrap: 'nowrap',
                  overflowX: 'auto',
                  mb: 2,
                  // Hide scrollbar while keeping scroll functionality
                  scrollbarWidth: 'none', // Firefox
                  msOverflowStyle: 'none', // IE and Edge
                  '&::-webkit-scrollbar': {
                    display: 'none' // Chrome, Safari, Opera
                  }
                }}>
                  <OuterNavChips 
                    routes={outerRoutes} 
                    selectedKey={selectedOuter} 
                    onSelect={onOuterRouteChange} 
                  />
                </Box>
              </Box>

              {/* Second Navigation - Inner Routes */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <InnerNavDesktop
                    routes={currentInnerRoutes}
                    selectedKey={selectedInner}
                    onSelect={onNavigateToInner}
                  />
                </Box>
              </Box>

              {/* Main Content */}
              <Box sx={{ flex: 1, pb: 2 }}>
                {/* Section-level Warning Panel (above form) */}
                <SectionWarnings
                  warnings={sectionWarnings}
                  onTokenClick={onSectionTokenClick}
                  isNorthernIreland={isNorthernIreland}
                />
                {renderBudgetForm()}
              </Box>

              {/* Desktop Footer - Below Content */}
              <Box sx={{
                mt: 2
              }}>
                <Footer
                  handleBack={onBackNav}
                  shouldApplyTopBorder={false}
                  shouldApplyBackgroundColor={false}
                  shouldBackDisabled={false}
                  nextButtonText={nextButtonLabel}
                  formId={currentFormId}
                  nextButtonLoading={nextButtonLoading}
                  nextButtonDisabled={nextButtonDisabled}
                  showSecondaryButton={false}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Loading Overlay for Navigation */}
      {isNavigating && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            // Use a light overlay so high‑contrast / accessibility modes
            // don’t appear as a solid black screen when navigating
            backgroundColor: 'rgba(255, 255, 255, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <CircularProgress size={50} sx={{ color: '#000' }} />
        </Box>
      )}
    </Box>
  );
};

export default DesktopLayout;

